import clsx from "clsx";
import { type FC } from "react";

import { ConfirmGroupButton } from "./ConfirmGroupButton";
import { PageLockedButton } from "./PageLockedButton";
import { StudentSubmitButton } from "./StudentSubmitButton";

import { t } from "i18n/i18n";
import { RoundedIconButton } from "mds/components/buttons/RoundedIconButton";
import { ChevronUpIcon } from "mds/icons";
import { useWorksheetLoaded } from "sharedb/ShareDBDoc";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import { pageSelectors, selectCurrentUserId, selectPageGroupUserForUser } from "store/selectors";
import { useShouldShowStudentSubmitButton } from "utils/page";

type PageButtonsProps = {
  containerHasVerticalScrollbar: boolean;
  pageId: string;
  showBackToTop: boolean;
  scrollBackToTop: () => void;
};

export const PageButtons: FC<PageButtonsProps> = ({
  containerHasVerticalScrollbar,
  pageId,
  showBackToTop,
  scrollBackToTop,
}) => {
  const userId = useAppSelector(selectCurrentUserId);
  const selectedPage = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const isDocLoaded = useWorksheetLoaded(selectedPage?.worksheet_id);
  const showStudentSubmitButton = useShouldShowStudentSubmitButton(selectedPage, isDocLoaded);
  const { canParticipateInSection } = useCapabilities();
  const showPageLockedButton = canParticipateInSection && selectedPage?.locked_at;
  const pageGroupUser = useAppSelector((s) =>
    selectPageGroupUserForUser(s, selectedPage?.id, userId),
  );
  const showConfirmGroupButton = pageGroupUser && !pageGroupUser.is_ready;
  const showButton = showStudentSubmitButton || showPageLockedButton || showConfirmGroupButton;

  if (!selectedPage || !userId) return null;

  return (
    <>
      {showButton && (
        <div
          className={clsx(
            "mt-[-10px] flex w-full max-w-[--page-extended-max-width] justify-center px-4 @page-default-max-width:px-0 md:px-10 md:@page-default-max-width:px-0",
            // The height of the blue divider determines how far the submit button is from the divider
            // and is different depending on whether the container has a vertical scrollbar or not.
            containerHasVerticalScrollbar ? "min-h-[46px]" : "min-h-[26px]",
          )}
        >
          <div className="h-[2px] w-full max-w-[--page-default-max-width] bg-blue-tint-90" />
        </div>
      )}
      <div
        className={clsx(
          "group/page-buttons pointer-events-none sticky bottom-5 flex w-full max-w-[--page-default-max-width] justify-end gap-2 px-4 @page-default-max-width:px-0 md:px-10 md:@page-default-max-width:px-0",
        )}
        data-back-to-top={showBackToTop || undefined}
      >
        {showButton ? (
          <div className="pointer-events-auto transition-transform delay-300 duration-300 ease-out group-data-[back-to-top]/page-buttons:translate-x-[-52px] group-data-[back-to-top]/page-buttons:delay-0">
            {showStudentSubmitButton ? (
              <StudentSubmitButton selectedPage={selectedPage} />
            ) : showPageLockedButton ? (
              <PageLockedButton />
            ) : showConfirmGroupButton ? (
              <ConfirmGroupButton selectedPage={selectedPage} userId={userId} />
            ) : null}
          </div>
        ) : null}
        <div
          className={clsx(
            "opacity-0 transition-opacity delay-0 duration-300 ease-out group-data-[back-to-top]/page-buttons:pointer-events-auto group-data-[back-to-top]/page-buttons:opacity-100 group-data-[back-to-top]/page-buttons:delay-300",
            showButton ? "absolute" : "",
          )}
        >
          <RoundedIconButton
            aria-label={t("common.back_to_top")}
            eventName="Back to top button"
            tabIndex={showBackToTop ? 0 : -1}
            onClick={scrollBackToTop}
          >
            <ChevronUpIcon />
          </RoundedIconButton>
        </div>
      </div>
    </>
  );
};
